/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';
import React, { useCallback, useMemo, useState } from 'react';
import { BirthDateField } from '../../../../../core/types/reservation-form-types';
import { getJapaneseEraYear } from '../../../../../utils/japanese-date-utils';
import { styles as inputStyles } from './input-styles';

export type BirthDateInputProps = {
  value: string; // 西暦日付（YYYY-MM-DD形式）
  onChange: (value: string) => void;
  onUpdateInvalidDate: (value: boolean) => void;
  fieldError: boolean;
  field: BirthDateField;
  onValidationError?: (message: string) => void;
  onClearError?: () => void;
};

export function BirthDateInput({
  value,
  onChange,
  onUpdateInvalidDate,
  fieldError,
  field,
  onValidationError,
  onClearError,
}: BirthDateInputProps) {
  // 選択肢の初期値
  const defaultValue = useMemo(() => {
    return parseDefaultValue(value);
  }, [value]);
  const [selectedYear, setSelectedYear] = useState<string>(defaultValue.year);
  const [selectedMonth, setSelectedMonth] = useState<string>(
    defaultValue.month
  );
  const [selectedDay, setSelectedDay] = useState<string>(defaultValue.day);

  // 日付の更新処理（共通関数）
  const updateDate = useCallback(
    (year: string, month: string, day: string) => {
      // 年月日がすべて入力されている場合
      if (year && month && day) {
        const yearNum = parseInt(year, 10);
        const monthNum = parseInt(month, 10);
        const dayNum = parseInt(day, 10);
        if (isValidDate(yearNum, monthNum, dayNum)) {
          onChange(formatDate(yearNum, monthNum, dayNum));
          onClearError?.();
          onUpdateInvalidDate(false);
        } else {
          // 不正な日付の場合はエラーを設定
          onValidationError?.('有効な日付を入力してください');
          onUpdateInvalidDate(true);
        }
      } else if (!year && !month && !day) {
        // 有効な値でなければクリアする
        onChange('');
        onUpdateInvalidDate(false);
      } else {
        // 入力が不完全な場合は不正な日付として扱う
        onUpdateInvalidDate(true);
      }
    },
    [onChange, onUpdateInvalidDate, onValidationError, onClearError]
  );

  // 年の選択肢の変更ハンドラ
  const handleYearChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newYear = e.target.value;
      setSelectedYear(newYear);
      updateDate(newYear, selectedMonth, selectedDay);
    },
    [selectedMonth, selectedDay, updateDate]
  );

  const handleMonthChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newMonth = e.target.value;
      setSelectedMonth(newMonth);
      updateDate(selectedYear, newMonth, selectedDay);
    },
    [selectedYear, selectedDay, updateDate]
  );

  const handleDayChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newDay = e.target.value;
      setSelectedDay(newDay);
      updateDate(selectedYear, selectedMonth, newDay);
    },
    [selectedYear, selectedMonth, updateDate]
  );

  const days = useMemo(() => {
    if (!selectedYear || !selectedMonth) {
      return Array.from({ length: 31 }, (_, i) => i + 1);
    }

    const year = parseInt(selectedYear, 10);
    const month = parseInt(selectedMonth, 10);
    const daysInMonth = new Date(year, month, 0).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  }, [selectedYear, selectedMonth]);

  const buildYearOption = (year: number | null) => {
    if (year === null) {
      return (
        <option key="separator" value="">
          年を選択
        </option>
      );
    }
    const japaneseEraText = getJapaneseEraYear(year);
    // 西暦を先にするか、和暦を先にするかを判定
    const label = field.enableDefaultJapaneseInput
      ? `${japaneseEraText}(西暦${year}年)`
      : `西暦${year}年(${japaneseEraText})`;
    return (
      <option key={year} value={year}>
        {label}
      </option>
    );
  };

  return (
    <div css={styles.dateInputContainer}>
      <select
        value={selectedYear}
        onChange={handleYearChange}
        css={[
          inputStyles.inputField,
          styles.select,
          fieldError && inputStyles.inputErrorField,
          styles.yearSelect,
          selectedYear === '' && styles.empty,
        ]}
      >
        {YEARS.map(buildYearOption)}
      </select>
      <select
        value={selectedMonth}
        onChange={handleMonthChange}
        css={[
          inputStyles.inputField,
          styles.select,
          fieldError && inputStyles.inputErrorField,
          styles.monthSelect,
          selectedMonth === '' && styles.empty,
        ]}
      >
        <option value="">月を選択</option>
        {MONTHS.map((month) => (
          <option key={month} value={month}>
            {month}月
          </option>
        ))}
      </select>
      <select
        value={selectedDay}
        onChange={handleDayChange}
        css={[
          inputStyles.inputField,
          styles.select,
          fieldError && inputStyles.inputErrorField,
          styles.daySelect,
          selectedDay === '' && styles.empty,
        ]}
      >
        <option value="">日を選択</option>
        {days.map((day) => (
          <option key={day} value={day}>
            {day}日
          </option>
        ))}
      </select>
    </div>
  );
}

// 日付の初期値をパースする関数
const parseDefaultValue = (
  value: string
): {
  year: string;
  month: string;
  day: string;
} => {
  if (value) {
    try {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return {
          year: date.getFullYear().toString(),
          month: (date.getMonth() + 1).toString(),
          day: date.getDate().toString(),
        };
      }
    } catch (error) {
      console.error('Error parsing western date:', error);
    }
  }
  return {
    year: '',
    month: '',
    day: '',
  };
};

// 年の選択肢
const YEARS = (() => {
  const currentYear = new Date().getFullYear();
  const years: (number | null)[] = [];

  // 100年前から1998年まで（古い年から順に）
  const oldestYear = currentYear - 99;
  for (let year = oldestYear; year <= 1998; year++) {
    years.push(year);
  }

  // 1999年と2000年の間に空の選択肢を挿入
  years.push(1999);
  years.push(null);

  // 2000年から現在まで
  for (let year = 2000; year <= currentYear; year++) {
    years.push(year);
  }

  return years;
})();

// 月の選択肢
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// 日付の妥当性チェック関数
const isValidDate = (year: number, month: number, day: number): boolean => {
  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
};

// 日付をフォーマットする関数
const formatDate = (year: number, month: number, day: number): string => {
  return `${year}-${month.toString().padStart(2, '0')}-${day
    .toString()
    .padStart(2, '0')}`;
};

// スタイル定義
const styles = {
  dateInputContainer: css`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    width: 100%;

    @media (min-width: 768px) {
      flex-wrap: nowrap;
    }
  `,
  select: css`
    background-color: #fff;
  `,
  yearSelect: css`
    width: 100%;
    height: 2.875em;

    @media (min-width: 768px) {
      width: 50%;
    }
  `,
  monthSelect: css`
    width: calc(50% - 4px);
    height: 2.875em;

    @media (min-width: 768px) {
      width: 25%;
    }
  `,
  daySelect: css`
    width: calc(50% - 4px);
    height: 2.875em;

    @media (min-width: 768px) {
      width: 25%;
    }
  `,
  empty: css`
    color: #999;
  `,
};
